import { useEffect, useMemo, useState } from "react";
import { PrismBanner, PrismButton, PrismCheckbox, PrismDialog, PrismInputFile, PrismRadio, PrismRadioGroup, PrismSelect } from "@prism-ui/react";
import { IMPORT_FIELDS, normalizeTagList } from "./spreadsheetImport";
import type { PeopleColumns, PersonColumn, ResubmissionGroup } from "./spreadsheetImport";
import { TRIAD_ROLES } from "../../../Projects/projects.utils";

export type CrossChapterSummaryEntry = { chapterId: number; count: number; location: string };

/** How much of a description the mapping sample shows before trailing off. */
const SAMPLE_DESCRIPTION_LIMIT = 180;

const truncate = (value: string, limit: number) =>
  value.length > limit ? `${value.slice(0, limit).trimEnd()}…` : value;

interface BulkImportModalProps {
  show: boolean;
  fileInputKey: number;
  importHeaders: string[];
  importMapping: Record<string, string>;
  onImportMappingChange: (field: string, value: string) => void;
  importPreviewRows: Record<string, string>[];
  importFileName: string;
  importing: boolean;
  hasImportRows: boolean;
  onImportFile: (file: File) => void;
  onTriggerConfirm: () => void;
  showImportConfirm: boolean;
  onCancelConfirm: () => void;
  onConfirmImport: () => void;
  mappedRowCount: number;
  crossChapterSummary: CrossChapterSummaryEntry[];
  resubmissionGroups: ResubmissionGroup[];
  keepAllFor: string[];
  onToggleKeepAll: (submitter: string, keepAll: boolean) => void;
  crossChapterChoice: "ignore" | "park" | "import";
  onCrossChapterChoiceChange: (value: "ignore" | "park" | "import") => void;
  canImportOtherChapters: boolean;
  peopleColumns: PeopleColumns;
  unmatchedTags: string[];
  selectedYear: number;
  includeLogos: boolean;
  onToggleIncludeLogos: (value: boolean) => void;
  logoArchiveName: string;
  onLogoArchiveSelected: (file: File | null) => void;
  onClose: () => void;
}

// Bulk Import: upload a CSV/XLSX spreadsheet, map its columns to project fields, and
// import the mapped rows as new projects on the active chapter/year's fair.
const BulkImportModal = ({
  show,
  fileInputKey,
  importHeaders,
  importMapping,
  onImportMappingChange,
  importPreviewRows,
  importFileName,
  importing,
  hasImportRows,
  onImportFile,
  onTriggerConfirm,
  showImportConfirm,
  onCancelConfirm,
  onConfirmImport,
  mappedRowCount,
  crossChapterSummary,
  resubmissionGroups,
  keepAllFor,
  onToggleKeepAll,
  crossChapterChoice,
  onCrossChapterChoiceChange,
  canImportOtherChapters,
  peopleColumns,
  unmatchedTags,
  selectedYear,
  includeLogos,
  onToggleIncludeLogos,
  logoArchiveName,
  onLogoArchiveSelected,
  onClose,
}: BulkImportModalProps) => {
  // The upload and the mapping are two different jobs: picking files is a one-shot action,
  // while the mapping needs the operator to read and confirm every column. Keeping them on
  // one screen buried the mapping under the upload controls.
  const [step, setStep] = useState<1 | 2>(1);

  // Reopening the dialog (or swapping the spreadsheet) always returns to the file step, so
  // the operator never lands on a mapping for a file they just replaced.
  useEffect(() => {
    if (!show) setStep(1);
  }, [show]);
  useEffect(() => {
    if (!importHeaders.length) setStep(1);
  }, [importHeaders]);

  // A triad is Mentee + Mentor + Committee Member. Listing the detected columns flat hid two
  // things an officer needs: which roles belong together, and that a role has no column at
  // all. A file carrying two triads would show six interleaved rows, and a file missing the
  // committee-member column just showed nothing where that role should be — indistinguishable
  // from a triad that genuinely has no committee member. Every triad therefore reports all
  // three slots, present or not.
  const { detectedTriads, untriadedMembers } = useMemo(() => {
    const byNumber = new Map<number, Map<string, PersonColumn>>();
    const untriaded: PersonColumn[] = [];

    for (const member of peopleColumns.members) {
      if (!TRIAD_ROLES.includes(member.role as (typeof TRIAD_ROLES)[number])) {
        untriaded.push(member);
        continue;
      }
      // A sheet with a single triad often omits the number from its headers.
      const number = member.triad ?? 1;
      let slots = byNumber.get(number);
      if (!slots) {
        slots = new Map();
        byNumber.set(number, slots);
      }
      if (slots.has(member.role)) untriaded.push(member);
      else slots.set(member.role, member);
    }

    return {
      detectedTriads: [...byNumber.entries()]
        .sort(([a], [b]) => a - b)
        .map(([number, slots]) => ({
          number,
          slots: TRIAD_ROLES.map((role) => ({ role, member: slots.get(role) })),
        })),
      untriadedMembers: untriaded,
    };
  }, [peopleColumns]);

  const missingRequired = importMapping.title ? [] : ["Title"];

  return (
  <PrismDialog open={show} close-method="user" onPrismClose={onClose} setAriaLabel="Bulk Import">
    <span slot="header">Bulk Import</span>
    <div className="project-fairs-modal-body">
    <section className="project-fairs-import">
      <ol className="project-fairs-steps" aria-label="Bulk import steps">
        <li aria-current={step === 1 ? "step" : undefined} className={step === 1 ? "is-active" : ""}>
          1. Choose your files
        </li>
        <li aria-current={step === 2 ? "step" : undefined} className={step === 2 ? "is-active" : ""}>
          2. Map the data
        </li>
      </ol>

      {step === 1 ? (
        <>
          <p>Use a spreadsheet to import one or more projects into the active chapter and year.</p>
          <PrismBanner tone="caution">
            Bulk import is a single-seed action: it only creates new projects. Any spreadsheet row whose title
            already matches a project on this fair is skipped and left untouched — re-running an import never
            overwrites an existing project. Edit those manually from Approved Projects instead. Team members are
            imported and grouped into triads when the spreadsheet names them; review each project in Approved
            Projects afterwards to confirm the grouping. Tags are matched against the existing tag list only —
            an import never creates a new tag.
          </PrismBanner>
          <PrismInputFile
            key={fileInputKey}
            label="Project spreadsheet"
            accept=".csv,.tsv,.xls,.xlsx"
            onPrismChange={(e: CustomEvent<{ files?: { list?: FileList } }>) => {
              const file = e.detail?.files?.list?.[0];
              if (file) onImportFile(file);
            }}
          />

          <section className="project-fairs-tile">
            <h4>Project logos</h4>
            <PrismCheckbox
              label="I want to provide logos"
              checked={includeLogos}
              onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => onToggleIncludeLogos(!!e.detail?.checked)}
            />
            {includeLogos ? (
              <div className="project-fairs-tile-fields">
                <PrismInputFile
                  key={`logos-${fileInputKey}`}
                  label="Logo archive (.zip)"
                  accept=".zip"
                  onPrismChange={(e: CustomEvent<{ files?: { list?: FileList } }>) => {
                    onLogoArchiveSelected(e.detail?.files?.list?.[0] || null);
                  }}
                />
                {logoArchiveName ? <p className="project-fairs-field-help">Selected: {logoArchiveName}</p> : null}
                <p className="project-fairs-field-help">
                  Logos are matched to projects by the filename the spreadsheet recorded for each one. Anything
                  we can&apos;t match confidently is handed to you to assign. Uploaded filenames are never kept —
                  each logo is saved under its project&apos;s own name, so personal information in a filename is
                  discarded.
                </p>
              </div>
            ) : null}
          </section>

          <div className="project-fairs-actions">
            <PrismButton
              display="fill"
              tone="secondary"
              disabled={!importHeaders.length}
              onPrismClick={() => setStep(2)}
            >
              Next: map the data
            </PrismButton>
          </div>
          {!importHeaders.length ? (
            <p className="project-fairs-field-help">Choose a spreadsheet to continue.</p>
          ) : null}
        </>
      ) : (
        <>
          <fieldset className="project-fairs-fieldset">
            <legend>Confirm the column mapping</legend>
            <p className="project-fairs-field-help">
              We matched each project field to a column in <strong>{importFileName || "your spreadsheet"}</strong>.
              Check every row below and correct anything we guessed wrong before importing. These are the
              fields a project has exactly one of — people are read separately, further down.
            </p>
            <div className="project-fairs-grid">
              {IMPORT_FIELDS.map(({ field, label }) => (
                <PrismSelect
                  key={field}
                  label={`${label} column`}
                  value={importMapping[field] || ""}
                  options={[
                    { value: "", display: `Select ${label.toLowerCase()} column` },
                    ...importHeaders.map((header) => ({ value: header, display: header })),
                  ]}
                  onPrismChange={(e: CustomEvent<{ value?: string }>) => onImportMappingChange(field, e.detail?.value || "")}
                />
              ))}
            </div>
            {missingRequired.length ? (
              <PrismBanner tone="caution">
                Still needed: {missingRequired.join(", ")}. Without it no rows can be imported.
              </PrismBanner>
            ) : null}
          </fieldset>

          {importPreviewRows.length ? (
            <fieldset className="project-fairs-fieldset">
              <legend>How the first rows read</legend>
              <p className="project-fairs-field-help">
                A sample of the first {importPreviewRows.length} row
                {importPreviewRows.length === 1 ? "" : "s"} as the mapping above reads them — not a limit, and
                nothing is imported yet. If a title, description or tag looks wrong or empty here, the mapping
                is pointing at the wrong column. Descriptions are shortened for this preview only; the full
                text is imported.
              </p>
              <div className="project-fairs-project-list">
                {importPreviewRows.map((row, index) => {
                  // Tags are shown the way they will be *stored*, not the way the sheet wrote
                  // them. A survey export prefixes every answer with the question it came from
                  // ("Subject Area: Mobile Apps"); the importer strips that, so showing the raw
                  // cell here would describe an import that never happens.
                  const tags = importMapping.tags ? normalizeTagList(row[importMapping.tags] || "") : [];
                  return (
                    <div key={`${row.title || "row"}-${index}`} className="project-fairs-stacked-item">
                      <strong>{row[importMapping.title] || "Untitled"}</strong>
                      <small>
                        {truncate(row[importMapping.description] || "No description", SAMPLE_DESCRIPTION_LIMIT)}
                      </small>
                      {importMapping.tags ? (
                        <small className="project-fairs-sample-tags">
                          Tags: {tags.length ? tags.join(", ") : "none"}
                        </small>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ) : null}

          <fieldset className="project-fairs-fieldset">
            <legend>Read automatically</legend>
            <p className="project-fairs-field-help">
              These aren&apos;t dropdowns because they aren&apos;t one column each. The mapping above covers
              the fields a project has exactly one of. People repeat — a sheet can carry any number of
              triads — so those columns are matched by their wording and the number in the header instead,
              and that number is read as the triad. A triad is a Mentee, a Mentor and a Committee Member;
              the executive sponsor sits outside them because one sponsor backs the whole team. Here is
              what was found in this file.
            </p>
            {peopleColumns.members.length || peopleColumns.sponsorName ? (
              <div className="project-fairs-project-list">
                {detectedTriads.map(({ number, slots }) => (
                  <div key={`triad-${number}`} className="project-fairs-stacked-item">
                    <strong>Triad {number}</strong>
                    {slots.map(({ role, member }) => (
                      <small key={`${number}-${role}`}>
                        {role}:{" "}
                        {member
                          ? `${member.nameHeader || "name column not found"} · Job title: ${
                              member.titleHeader || "not found"
                            }`
                          : "no column found — nobody will be imported for this role"}
                      </small>
                    ))}
                  </div>
                ))}
                {untriadedMembers.map((member) => (
                  <div
                    key={`other-${member.role}-${member.triad ?? "none"}`}
                    className="project-fairs-stacked-item"
                  >
                    <strong>{member.role}</strong>
                    <small>
                      Name: {member.nameHeader || "not found"} · Job title:{" "}
                      {member.titleHeader || "not found"}
                    </small>
                  </div>
                ))}
                {peopleColumns.sponsorName ? (
                  <div className="project-fairs-stacked-item">
                    <strong>Executive sponsor — sponsors the whole team</strong>
                    <small>
                      Name: {peopleColumns.sponsorName} · Job title:{" "}
                      {peopleColumns.sponsorTitle || "not found"}
                    </small>
                    <small>
                      Not part of a triad: one sponsor backs every triad on the project.
                    </small>
                  </div>
                ) : null}
              </div>
            ) : (
              <PrismBanner tone="info">
                No people columns were recognised in this file, so no team members or sponsors will be
                imported. You can add them per project afterwards in Approved Projects.
              </PrismBanner>
            )}
            <p className="project-fairs-field-help">
              Grouping read from a spreadsheet is a best guess. Open each project in Approved Projects
              afterwards and confirm the triads.
            </p>
          </fieldset>

      {resubmissionGroups.length ? (
        <section className="project-fairs-tile">
          <h4>Repeat submissions</h4>
          <p className="project-fairs-field-help">
            These people filled the form more than once. Only their most recent response is imported.
          </p>
          <div className="project-fairs-project-list">
            {resubmissionGroups.map((group) => (
              <div key={group.submitter} className="project-fairs-project-item">
                <strong>{group.submitter || "Unnamed submitter"}</strong>
                <small>
                  Importing <strong>{group.keptTitle || "Untitled"}</strong> — superseding{" "}
                  {group.supersededTitles.map((title) => title || "Untitled").join(", ")}
                </small>
                {group.titlesDiffer ? (
                  <>
                    <small>
                      The earlier response used a different project title. That is usually a
                      correction, but it can also mean this person submitted two separate projects.
                    </small>
                    <PrismCheckbox
                      label={`Keep all ${group.superseded.length + 1} submissions from ${
                        group.submitter || "this person"
                      }`}
                      checked={keepAllFor.includes(group.submitter)}
                      onPrismChange={(e: CustomEvent<{ checked?: boolean }>) =>
                        onToggleKeepAll(group.submitter, !!e.detail?.checked)
                      }
                    />
                  </>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {crossChapterSummary.length ? (
        <section className="project-fairs-tile">
          <h4>Rows for other chapters</h4>
          <p className="project-fairs-field-help">
            This spreadsheet also names{" "}
            {crossChapterSummary
              .map((entry) => `${entry.count} project${entry.count === 1 ? "" : "s"} for ${entry.location}`)
              .join(", ")}
            . Those rows don&apos;t belong to the fair you have open — choose what should happen to them.
          </p>
          <PrismRadioGroup
            legend="Rows belonging to another chapter"
            value={crossChapterChoice}
            onPrismChange={(e: CustomEvent<{ value?: string }>) => {
              const value = e.detail?.value;
              if (value === "ignore") return onCrossChapterChoiceChange("ignore");
              if (value === "import" && canImportOtherChapters) return onCrossChapterChoiceChange("import");
              return onCrossChapterChoiceChange("park");
            }}
          >
            <PrismRadio
              name="cross-chapter-choice"
              value="park"
              checked={crossChapterChoice === "park"}
              label="Send them to that chapter's review queue"
            />
            {/* Acting for another chapter is an admin power. Officers own one chapter's roster,
                so for them the queue is the only route into someone else's fair. */}
            {canImportOtherChapters ? (
              <PrismRadio
                name="cross-chapter-choice"
                value="import"
                checked={crossChapterChoice === "import"}
                label="Import them now into their own chapter's fair (admin)"
              />
            ) : null}
            <PrismRadio
              name="cross-chapter-choice"
              value="ignore"
              checked={crossChapterChoice === "ignore"}
              label="Ignore them"
            />
          </PrismRadioGroup>
          {crossChapterChoice === "import" ? (
            <PrismBanner tone="caution">
              These projects will be created on each chapter&apos;s own fair straight away, with no
              officer review. Their own officers will not be asked first.
            </PrismBanner>
          ) : null}
        </section>
      ) : null}

      {unmatchedTags.length ? (
        <PrismBanner tone="caution">
          These tags aren&apos;t in the tag list and will be skipped: {unmatchedTags.join(", ")}. Add them
          under Tags first if you want them applied, then set them on each project.
        </PrismBanner>
      ) : null}

      <div className="project-fairs-actions">
        <PrismButton display="outline" tone="secondary" disabled={importing} onPrismClick={() => setStep(1)}>
          Back
        </PrismButton>
        <PrismButton
          display="fill"
          tone="secondary"
          disabled={importing || !hasImportRows}
          loading={importing}
          onPrismClick={onTriggerConfirm}
        >
          Import projects
        </PrismButton>
      </div>
        </>
      )}
    </section>
    </div>

    <PrismDialog
      open={showImportConfirm}
      close-method="user"
      onPrismClose={() => onCancelConfirm()}
      setAriaLabel="Confirm project import"
    >
      <span slot="header">Confirm spreadsheet import</span>
      <div style={{ display: "grid", gap: "1rem", minWidth: "28rem" }}>
        <p>
          You are about to import {mappedRowCount} project(s) into the {selectedYear} cohort.
        </p>
        <p>This creates new project records for titles that don&apos;t already exist on this fair. Rows whose
          title matches an existing project are skipped, not overwritten.</p>
        {resubmissionGroups.length ? (
          <PrismBanner tone="info">
            {resubmissionGroups.filter((group) => !keepAllFor.includes(group.submitter)).length} repeat
            submission(s) collapsed to the submitter&apos;s most recent response.
          </PrismBanner>
        ) : null}
        {crossChapterSummary.length ? (
          <PrismBanner tone={crossChapterChoice === "ignore" ? "caution" : "info"}>
            {crossChapterChoice === "park"
              ? `${crossChapterSummary
                  .map((entry) => `${entry.count} row${entry.count === 1 ? "" : "s"} for ${entry.location}`)
                  .join(", ")} will be sent to that chapter's officers to approve. Nothing is imported for them here.`
              : crossChapterChoice === "import"
              ? `${crossChapterSummary
                  .map((entry) => `${entry.count} row${entry.count === 1 ? "" : "s"} for ${entry.location}`)
                  .join(", ")} will be created on that chapter's own fair now, without their officers reviewing them first.`
              : `${crossChapterSummary
                  .map((entry) => `${entry.count} row${entry.count === 1 ? "" : "s"} for ${entry.location}`)
                  .join(", ")} will be discarded.`}
          </PrismBanner>
        ) : null}
        {unmatchedTags.length ? (
          <PrismBanner tone="caution">
            Unknown tags will be skipped: {unmatchedTags.join(", ")}.
          </PrismBanner>
        ) : null}
        {includeLogos && logoArchiveName ? (
          <PrismBanner tone="info">
            After the rows import, {logoArchiveName} will be matched against them. Anything we can&apos;t place
            confidently will be handed back to you.
          </PrismBanner>
        ) : null}
      </div>
      <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={onCancelConfirm}>
        Cancel
      </PrismButton>
      <PrismButton slot="footer" tone="primary-brand" display="fill" onPrismClick={onConfirmImport}>
        Confirm import
      </PrismButton>
    </PrismDialog>

    <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={onClose}>
      Close
    </PrismButton>
  </PrismDialog>
  );
};

export default BulkImportModal;
